import { useCallback, useEffect, useState } from "react";
import { useAppContext } from "./useAppContext";
import { useMessage } from "./useMessage";

function getUsedFontFamilies(
	definition: eXpress.core.IDefinition | null,
	fromDefinition: boolean,
	fromConfiguration: boolean,
	includeDefaultFont: boolean,
): readonly eXpress.core.FontFamily[] {
	if (!definition) return [];

	// Get Fonts used in Definition
	const usedInDefinition: readonly eXpress.core.FontFamily[] = fromDefinition
		? definition.getUsedAssets(eXpress.core.FontFamily)
		: [];

	// Get Fonts used in Configuration
	const usedInConfiguration: readonly eXpress.core.FontFamily[] =
		fromConfiguration && definition.configuration
			? definition.configuration.getUsedAssets(eXpress.core.FontFamily)
			: [];

	const defaultFonts: readonly eXpress.core.FontFamily[] = includeDefaultFont
		? [definition.editor.fallbackFontFamily]
		: [];

	return [...defaultFonts, ...usedInDefinition, ...usedInConfiguration]
		.distinct()
		.orderBy((ff1, ff2) => eXpress.utils.ascendingLocaleStringCompare(ff1.name, ff2.name)); // Ensure the same order every time (for comparer)
}

function canChangeUsedFontFamilies(message: eXpress.core.IMessage) {
	return (
		eXpress.core.isDefinitionChanged(message) || // Used in  Definition / Configuration
		eXpress.core.canZoneBeAddedOrRemoved(message) || // If a zone is removed, used fonts can change
		eXpress.core.canPageBeAddedOrRemoved(message) || // If a page is removed, used fonts can change
		message instanceof eXpress.core.FontFamilyChangeMessage // Used font is changed
	);
}

/*
 * Because hooks cannot be conditionally loaded, we provide booleans that indicate what to load
 */
export function useUsedFontFamilies({
	fromDefinition = true,
	fromConfiguration = false,
	includeDefaultFont = false,
}: {
	fromDefinition?: boolean;
	fromConfiguration?: boolean;
	includeDefaultFont?: boolean;
} = {}): readonly eXpress.core.FontFamily[] {
	const { editor } = useAppContext();

	// use setState to trigger a re-render
	const [usedFontFamilies, setUsedFontFamilies] = useState<readonly eXpress.core.FontFamily[]>(
		[],
	);

	// Update if one of the arguments changes
	useEffect(() => {
		const newUsedFontFamilies = getUsedFontFamilies(
			editor.definition,
			fromDefinition,
			fromConfiguration,
			includeDefaultFont,
		);
		setUsedFontFamilies(newUsedFontFamilies);
	}, [editor, fromDefinition, fromConfiguration, includeDefaultFont]);

	const onMessage = useCallback(
		(message: eXpress.core.IMessage) => {
			const shouldUpdateUsedFonts = canChangeUsedFontFamilies(message);

			if (shouldUpdateUsedFonts) {
				const newUsedFontFamilies = getUsedFontFamilies(
					editor.definition,
					fromDefinition,
					fromConfiguration,
					includeDefaultFont,
				);

				// Prevent rerender if array contains the same values
				if (!eXpress.collections.arrays.equals(usedFontFamilies, newUsedFontFamilies)) {
					setUsedFontFamilies(newUsedFontFamilies);
				}
				return;
			}

			// Allow to disable it on development builds, if isDebugMode is changed at runtime, it will cause errors because of conditional hooks
			if (eXpress.isDebugMode) {
				if (process.env.PUBLIC_DEBUG === "true") {
					// This code should detect if there are messages that also change the used fonts
					const newUsedFontFamilies = getUsedFontFamilies(
						editor.definition,
						fromDefinition,
						fromConfiguration,
						includeDefaultFont,
					);
					// Remark:
					// Because useState is used, the previous value will be the value before previous reconciliation
					// Not from before this message, so this can give false results
					if (!eXpress.collections.arrays.equals(usedFontFamilies, newUsedFontFamilies)) {
						const messageName = eXpress.core.getMessageName(message);

						const errorMessage = `useUsedFontFamilies: Message '${messageName}' did update the used font families but is not included.`;
						editor.logger.error(errorMessage);
						editor.logger.error(
							`Previous fonts: ${usedFontFamilies.map((ff) => ff.name).join(", ")}`,
						);
						editor.logger.error(
							`New      fonts: ${newUsedFontFamilies
								.map((ff) => ff.name)
								.join(", ")}`,
						);
						throw new eXpress.UnhandledMessageException(errorMessage);
					}
				}
			}
		},
		[editor, usedFontFamilies, fromDefinition, fromConfiguration, includeDefaultFont],
	);

	useMessage(onMessage);

	return usedFontFamilies;
}
